import { z } from 'zod';
import { jsonEncoded } from '@repo/lib/zod';

class MemoryStorage implements Storage {
  private readonly items = new Map<string, string>();

  get length(): number {
    return this.items.size;
  }

  clear(): void {
    this.items.clear();
  }

  getItem(key: string): string | null {
    return this.items.get(key) ?? null;
  }

  key(index: number): string | null {
    return [...this.items.keys()][index] ?? null;
  }

  removeItem(key: string): void {
    this.items.delete(key);
  }

  setItem(key: string, value: string): void {
    this.items.set(key, String(value));
  }
}

/**
 * Safe wrapper around a Storage object that catches and handles errors gracefully.
 * This prevents storage-related errors from crashing the application in environments where
 * storage may be disabled, full, or otherwise unavailable.
 *
 * Use {@link SafeStorage.get} to get a cached instance for a given Storage, or the
 * {@link safeLocalStorage} / {@link safeSessionStorage} exports for the native storages.
 */
export class SafeStorage implements Storage {
  private resolved: Storage | undefined;

  private constructor(private readonly resolve: () => Storage | null) {}

  private static cache = new WeakMap<Storage, SafeStorage>();

  /** Prefer the {@link safeLocalStorage} export over calling this directly. */
  static readonly local = new SafeStorage(() => localStorage);

  /** Prefer the {@link safeSessionStorage} export over calling this directly. */
  static readonly session = new SafeStorage(() => sessionStorage);

  /**
   * Returns a SafeStorage instance for the given Storage, reusing a cached instance when present.
   *
   * @param storage - The Storage object to wrap (e.g., localStorage, sessionStorage)
   * @returns A Storage-compatible SafeStorage instance
   *
   * @example
   * ```typescript
   * const safeLocalStorage = SafeStorage.get(localStorage);
   * safeLocalStorage.setItem('key', 'value'); // Won't throw if storage is disabled
   * const value = safeLocalStorage.getItem('key'); // Returns null on error
   * ```
   */
  static get(storage: Storage): SafeStorage {
    if (storage instanceof SafeStorage) {
      return storage;
    }
    let instance = this.cache.get(storage);
    if (!instance) {
      instance = new SafeStorage(() => storage);
      this.cache.set(storage, instance);
    }
    return instance;
  }

  private get storage(): Storage {
    if (!this.resolved) {
      try {
        this.resolved = this.resolve() ?? new MemoryStorage();
      } catch {
        this.resolved = new MemoryStorage();
      }
    }
    return this.resolved;
  }

  get length(): number {
    try {
      return this.storage.length;
    } catch {
      return 0;
    }
  }

  clear(): void {
    try {
      this.storage.clear();
    } catch {
      // Storage unavailable or disabled
    }
  }

  getItem(key: string): string | null {
    try {
      return this.storage.getItem(key);
    } catch {
      return null;
    }
  }

  key(index: number): string | null {
    try {
      return this.storage.key(index);
    } catch {
      return null;
    }
  }

  removeItem(key: string): void {
    try {
      this.storage.removeItem(key);
    } catch {
      // Storage unavailable or disabled
    }
  }

  setItem(key: string, value: string): void {
    try {
      this.storage.setItem(key, value);
    } catch {
      // Storage unavailable or disabled
    }
  }
}

/**
 * Pre-configured safe localStorage instance that wraps the native localStorage
 * with error handling to prevent storage-related errors from crashing the application.
 * The native localStorage is resolved on first use, so importing this is safe anywhere; when
 * it is blocked or absent, this falls back to in-memory storage for the page's lifetime.
 *
 * @example
 * ```typescript
 * import { safeLocalStorage } from '@repo/dom/storage';
 *
 * safeLocalStorage.setItem('key', 'value'); // Won't throw if storage is disabled
 * const value = safeLocalStorage.getItem('key'); // Returns null on error
 * ```
 */
export const safeLocalStorage = SafeStorage.local;

/**
 * Pre-configured safe sessionStorage instance that wraps the native sessionStorage
 * with error handling to prevent storage-related errors from crashing the application.
 * The native sessionStorage is resolved on first use, so importing this is safe anywhere; when
 * it is blocked or absent, this falls back to in-memory storage for the page's lifetime.
 *
 * @example
 * ```typescript
 * import { safeSessionStorage } from '@repo/dom/storage';
 *
 * safeSessionStorage.setItem('temp', 'data'); // Won't throw if storage is disabled
 * const value = safeSessionStorage.getItem('temp'); // Returns null on error
 * ```
 */
export const safeSessionStorage = SafeStorage.session;

/**
 * Retrieves and parses a JSON value from storage with Zod schema validation.
 * Provides type safety by validating the parsed data against the provided schema.
 *
 * @template T - The Zod schema type
 * @param storage - Storage instance to retrieve from (e.g., localStorage, sessionStorage)
 * @param key - Storage key to retrieve the value from
 * @param schema - Zod schema to validate and parse the stored data
 * @returns The parsed and validated data of type T, or null if key doesn't exist or validation fails
 *
 * @example
 * ```typescript
 * const userSchema = z.object({ name: z.string(), age: z.number() });
 * const user = getStorageJson(safeLocalStorage, 'user', userSchema);
 * // user is typed as { name: string; age: number } | null
 * ```
 */
export function getStorageJson<T extends z.ZodTypeAny>(
  storage: Storage,
  key: string,
  schema: T,
): z.infer<T> | null {
  const value = SafeStorage.get(storage).getItem(key);
  if (value === null) return null;
  const result = jsonEncoded(schema).safeParse(value);
  if (!result.success) return null;
  return result.data;
}

/**
 * Serializes and stores a value as JSON in storage.
 *
 * @param storage - Storage instance to store in (e.g., localStorage, sessionStorage)
 * @param key - Storage key to store the value under
 * @param value - Value to serialize and store
 *
 * @example
 * ```typescript
 * const user = { name: 'John', age: 30 };
 * setStorageJson(safeLocalStorage, 'user', user);
 * ```
 */
export function setStorageJson(storage: Storage, key: string, value: unknown) {
  const str = JSON.stringify(value);
  SafeStorage.get(storage).setItem(key, str);
}
